import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ENVIRONMENT } from '../../../../environment/environment';
import { ApiResponse, Pagination } from '../../models/api-response.model';
import { GetDealsCountResponse, GetDealByGroupIdResponse, Deal, DealsFilterModel, GetSsoLinkRequest, GetSsoLinkResponse } from '../../models/deals.model';

@Injectable({
    providedIn: 'root',
})
export class DealsService {
    private apiRoute = `${ENVIRONMENT.apiDomain}/api/v1/deal`;

    constructor(private httpClient: HttpClient) {}

    public getDealsCount(): Observable<ApiResponse<GetDealsCountResponse>> {
        // Mock implementation as requested - commenting out the actual API call
        return this.httpClient.get<ApiResponse<GetDealsCountResponse>>(`${this.apiRoute}/active/total`).pipe(
            map((response) => {
                // Debug Log
                // console.log("[On Get Deals Count] Response: ", response);

                return response || { result: { total: 0 } };
            }),
            catchError(() => of({ result: { total: 0 } }))
        );
    }

    /**
     * Get all available deals with pagination
     * @param requestBody - Pagination filter with skip and take parameters
     * @returns Observable<ApiResponse<Pagination<Deal[]>>>
     */
    public getDeals(requestBody: DealsFilterModel): Observable<ApiResponse<Pagination<Deal[]>>> {
        return this.httpClient.post<ApiResponse<Pagination<Deal[]>>>(`${this.apiRoute}`, requestBody).pipe(
            map((response) => {
                // Debug Log
                // console.log("[On Get Deals] Response: ", response);

                return response || { result: { data: [], skip: 0, take: 0, totalCount: 0, pageIndex: 0, perPage: 0, totalPages: 0 } };
            }),
            catchError(() => of({ result: { data: [], skip: 0, take: 0, totalCount: 0, pageIndex: 0, perPage: 0, totalPages: 0 } }))
        );
    }

    /**
     * Get deal details by ID
     * @param dealGroupId - The Group ID of the deal to fetch
     * @returns Observable<ApiResponse<GetDealByGroupIdResponse>>
     */
    public getDealByGroupId(dealGroupId: string): Observable<ApiResponse<GetDealByGroupIdResponse>> {
        // Commented out actual API call - replace with hardcoded data for testing
        return this.httpClient.get<ApiResponse<Deal>>(`${this.apiRoute}/${dealGroupId}`).pipe(
            map((response) => {
                // Debug Log
                // console.log("[On Get Deal Details] Response: ", response);
                
                // The API returns the deal data directly in response.result
                // We need to wrap it in the expected GetDealByGroupIdResponse format
                return response && response.result ? 
                    { result: { deal: response.result } } : 
                    { result: { deal: null } };
            }),
            catchError(() => of({ result: { deal: null } }))
        );
    }

    /**
     * Get SSO link for a deal
     * @param dealId - The ID of the deal
     * @param forceError - Flag to force an error response for testing
     * @returns Observable<ApiResponse<GetSsoLinkResponse>>
     */
    public getSsoLink(dealId: string, forceError: boolean): Observable<ApiResponse<GetSsoLinkResponse>> {
        const requestBody: GetSsoLinkRequest = {
            dealId: dealId,
            forceError: forceError
        };
        
        return this.httpClient.post<ApiResponse<GetSsoLinkResponse>>(
            `${this.apiRoute}/sso-link`,
            requestBody
        ).pipe(
            map((response) => {
                // Debug Log
                // console.log("[On Get SSO Link] Response: ", response);
                
                return response;
            })
        );
    }
}
